import type { QuoteItem, QuoteRichBlock, QuoteRichRun } from '../types';

/**
 * Formatierte Leistungsbeschreibung einer Angebotsposition (Fett, Kursiv,
 * Aufzaehlung, Farbe).
 *
 * `QuoteItem.title` bleibt der Klartext — ihn lesen Auftragsuebernahme,
 * Projekt-LV, Rechnung und Suche. `titleRich` traegt nur die Formatierung fuer
 * das Angebots-PDF und gilt nur, solange sein Klartext noch genau `title` ist
 * (effectiveQuoteRich). Aendert ein anderer Weg den Titel (Merge, Import,
 * Inline-Feld), faellt das PDF damit von selbst auf den alten Druck zurueck,
 * statt einen veralteten Text zu drucken.
 *
 * Ohne Formatierung druckt das PDF den Titel wie bisher komplett fett. Der
 * Editor zeigt einen unformatierten Titel deshalb auch fett an, und eine
 * Formatierung, die genau diesem Druck entspricht, wird gar nicht erst
 * gespeichert.
 */

/** Klartext-Praefix einer Aufzaehlungszeile in `title`. */
export const QUOTE_RICH_BULLET = '• ';
/** Klartext-Praefix einer Folgezeile im selben Aufzaehlungspunkt (Umschalt+Enter). */
export const QUOTE_RICH_CONT = '  ';

/** Textfarbe des Editors; eine Auswahl dieser Farbe heisst "keine Farbe". */
export const QUOTE_RICH_DEFAULT_COLOR = '#1e293b';

/** Farbauswahl im Editor. `null` = normale Textfarbe des Dokuments. */
export const QUOTE_RICH_COLORS: { label: string; value: string | null }[] = [
  { label: 'Standard', value: null },
  { label: 'Rot', value: '#dc2626' },
  { label: 'Blau', value: '#1d4ed8' },
  { label: 'Grün', value: '#15803d' },
  { label: 'Orange', value: '#c2410c' },
  { label: 'Grau', value: '#64748b' },
];

/** '#rgb', '#rrggbb' oder 'rgb(r, g, b)' als '#rrggbb'; alles andere undefined. */
export const normalizeRichColor = (raw: string | null | undefined): string | undefined => {
  if (!raw) return undefined;
  const s = String(raw).trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(s)) return s;
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(s);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`;
  const rgb = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*([\d.]+)\s*)?\)$/.exec(s);
  if (rgb) {
    if (rgb[4] !== undefined && Number(rgb[4]) === 0) return undefined;
    return `#${[rgb[1], rgb[2], rgb[3]]
      .map((n) => Math.min(255, Number(n)).toString(16).padStart(2, '0'))
      .join('')}`;
  }
  return undefined;
};

const sameStyle = (a: QuoteRichRun, b: QuoteRichRun): boolean =>
  !!a.bold === !!b.bold && !!a.italic === !!b.italic && (a.color ?? '') === (b.color ?? '');

const cleanRun = (raw: unknown): QuoteRichRun | null => {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<QuoteRichRun>;
  // Ein Lauf ist immer einzeilig: Zeilen sind Bloecke. Geschuetzte und
  // unsichtbare Leerzeichen aus contentEditable werden zu normalen.
  const text = String(r.text ?? '')
    .replace(/\r\n|\r|\n/g, ' ')
    .replace(/ /g, ' ')
    .replace(/[​﻿]/g, '');
  if (!text) return null;
  const color = normalizeRichColor(r.color);
  return {
    text,
    ...(r.bold ? { bold: true } : {}),
    ...(r.italic ? { italic: true } : {}),
    ...(color && color !== QUOTE_RICH_DEFAULT_COLOR ? { color } : {}),
  };
};

const isBlankBlock = (b: QuoteRichBlock): boolean => b.runs.every((r) => !r.text.trim());

/**
 * Bringt beliebige Eingabe (gespeichertes JSON, Editor-DOM) in die kanonische
 * Form: gueltige Farben, gleich formatierte Nachbarlaeufe zusammengelegt,
 * leere Laeufe und leere Rand-Bloecke entfernt.
 */
export const normalizeQuoteRich = (blocks: unknown): QuoteRichBlock[] => {
  if (!Array.isArray(blocks)) return [];
  const out: QuoteRichBlock[] = [];
  for (const raw of blocks) {
    if (!raw || typeof raw !== 'object') continue;
    const b = raw as Partial<QuoteRichBlock>;
    const runs: QuoteRichRun[] = [];
    for (const rr of Array.isArray(b.runs) ? b.runs : []) {
      const r = cleanRun(rr);
      if (!r) continue;
      const last = runs[runs.length - 1];
      if (last && sameStyle(last, r)) last.text += r.text;
      else runs.push(r);
    }
    out.push({ ...(b.list ? { list: true } : {}), ...(b.list && b.cont ? { cont: true } : {}), runs });
  }
  while (out.length && isBlankBlock(out[0])) out.shift();
  while (out.length && isBlankBlock(out[out.length - 1])) out.pop();
  // Eine Folgezeile gibt es nur hinter einem Aufzaehlungspunkt (oder dessen
  // Folgezeile); sonst wird sie selbst zum Punkt.
  out.forEach((b, i) => {
    if (b.cont && !out[i - 1]?.list) delete b.cont;
  });
  return out;
};

/** Klartext fuer `title`: eine Zeile je Block, Aufzaehlungen mit "• ", Folgezeilen eingerueckt. */
export const quoteRichToPlain = (blocks: QuoteRichBlock[]): string =>
  blocks
    .map(
      (b) =>
        (b.cont ? QUOTE_RICH_CONT : b.list ? QUOTE_RICH_BULLET : '') + b.runs.map((r) => r.text).join(''),
    )
    .join('\n');

/**
 * Startinhalt des Editors fuer einen unformatierten Titel: was das PDF heute
 * druckt — jede Zeile fett. Leerzeilen bleiben stehen (sonst gingen sie mit
 * der ersten Aenderung im Editor verloren). Zeilen mit "• " (aus einer
 * frueheren Formatierung) werden wieder zu Aufzaehlungspunkten, eingerueckte
 * Zeilen dahinter zu deren Folgezeilen.
 */
export const quoteRichFromPlain = (title: string): QuoteRichBlock[] => {
  const blocks: QuoteRichBlock[] = [];
  for (const l of String(title || '').split(/\r\n|\r|\n/)) {
    const prev = blocks[blocks.length - 1];
    if (!l.trim()) blocks.push({ runs: [] });
    else if (l.startsWith(QUOTE_RICH_BULLET))
      blocks.push({ list: true, runs: [{ text: l.slice(QUOTE_RICH_BULLET.length), bold: true }] });
    else if (prev?.list && l.startsWith(QUOTE_RICH_CONT))
      blocks.push({ list: true, cont: true, runs: [{ text: l.slice(QUOTE_RICH_CONT.length), bold: true }] });
    else blocks.push({ runs: [{ text: l, bold: true }] });
  }
  return normalizeQuoteRich(blocks);
};

/** Entspricht die Formatierung dem bisherigen Druck (alles fett, sonst nichts)? */
export const quoteRichIsPlainDefault = (blocks: QuoteRichBlock[]): boolean =>
  blocks.every(
    (b) =>
      !b.list &&
      b.runs.length > 0 &&
      b.runs.every((r) => !r.italic && !r.color && (r.bold || !r.text.trim())),
  );

/** Formatierung, die das PDF fuer diese Position verwenden darf — sonst null. */
export const effectiveQuoteRich = (
  item: Pick<QuoteItem, 'title' | 'titleRich'>,
): QuoteRichBlock[] | null => {
  if (!item.titleRich) return null;
  const blocks = normalizeQuoteRich(item.titleRich);
  if (blocks.length === 0) return null;
  if (quoteRichToPlain(blocks) !== (item.title ?? '')) return null;
  return blocks;
};

/** Editor-Inhalt als Positionsfelder: Klartext immer, Formatierung nur bei Bedarf. */
export const quoteTextFromRich = (
  blocks: QuoteRichBlock[],
): { title: string; titleRich?: QuoteRichBlock[] } => {
  const norm = normalizeQuoteRich(blocks);
  const title = quoteRichToPlain(norm);
  return norm.length > 0 && !quoteRichIsPlainDefault(norm) ? { title, titleRich: norm } : { title };
};

// ── Editor-DOM ↔ Bloecke ─────────────────────────────────────────────────────

/**
 * Der Teil eines DOM-Knotens, den der Parser liest. Echte DOM-Knoten erfuellen
 * ihn; Tests bauen ihn als einfaches Objekt (kein jsdom im Projekt).
 */
export interface RichDomNode {
  nodeType: number;
  nodeName: string;
  textContent: string | null;
  childNodes: ArrayLike<RichDomNode>;
  getAttribute?: (name: string) => string | null;
  style?: { fontWeight?: string; fontStyle?: string; color?: string };
}

type DomStyle = { bold: boolean; italic: boolean; color?: string };

const BLOCK_TAGS = new Set(['DIV', 'P', 'LI', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE']);

const styleOf = (node: RichDomNode, st: DomStyle): DomStyle => {
  const name = node.nodeName.toUpperCase();
  let { bold, italic, color } = st;
  if (name === 'B' || name === 'STRONG') bold = true;
  if (name === 'I' || name === 'EM') italic = true;
  if (name === 'FONT') {
    const c = normalizeRichColor(node.getAttribute?.('color'));
    if (c) color = c;
  }
  const s = node.style;
  if (s) {
    // Inline-Stile schreibt der Browser beim Aufheben einer Formatierung
    // innerhalb eines formatierten Bereichs (font-weight: normal).
    const fw = (s.fontWeight || '').trim();
    if (fw === 'bold' || fw === 'bolder' || Number(fw) >= 600) bold = true;
    else if (fw === 'normal' || fw === 'lighter' || (fw !== '' && Number(fw) < 600)) bold = false;
    const fs = (s.fontStyle || '').trim();
    if (fs === 'italic' || fs === 'oblique') italic = true;
    else if (fs === 'normal') italic = false;
    const c = normalizeRichColor(s.color);
    if (c) color = c;
  }
  return { bold, italic, color };
};

/**
 * Liest den Inhalt des contentEditable-Editors. Nur Text, Fett/Kursiv/Farbe
 * und Zeilen-/Listenstruktur werden uebernommen; alles andere faellt weg.
 * Bloecke entstehen an DIV/P/LI und an Zeilenumbruechen (BR).
 */
export const parseQuoteRichDom = (root: RichDomNode): QuoteRichBlock[] => {
  const blocks: QuoteRichBlock[] = [];
  let open: QuoteRichBlock | null = null;
  // Ein LI legt seinen ersten Block sofort an. Jeder weitere Block innerhalb
  // desselben LI (nach BR = Umschalt+Enter, oder einem DIV darin) ist eine
  // Folgezeile dieses Punkts, kein neuer Punkt.
  const start = (list: boolean, cont = list): QuoteRichBlock => {
    const b: QuoteRichBlock = { ...(list ? { list: true } : {}), ...(list && cont ? { cont: true } : {}), runs: [] };
    blocks.push(b);
    open = b;
    return b;
  };
  const walk = (node: RichDomNode, st: DomStyle, inLi: boolean): void => {
    if (node.nodeType === 3) {
      const text = node.textContent || '';
      if (!text) return;
      const b = open ?? start(inLi);
      b.runs.push({ text, bold: st.bold, italic: st.italic, color: st.color });
      return;
    }
    if (node.nodeType !== 1) return;
    const name = node.nodeName.toUpperCase();
    if (name === 'BR') {
      // Ein BR beendet die laufende Zeile; steht keine offen, ist er selbst
      // eine (leere) Zeile.
      if (!open) start(inLi);
      open = null;
      return;
    }
    const isBlock = BLOCK_TAGS.has(name);
    // Ein offener Block ohne Inhalt kann nur der frisch angelegte erste Block
    // eines LI sein (Text landet sofort, BR schliesst). Steckt der Inhalt des
    // LI in einem DIV, gehoert er in genau diesen Block — sonst bliebe ein
    // leerer Phantom-Punkt mit dem Text als Folgezeile.
    if (isBlock && !(open && open.runs.length === 0)) open = null;
    if (name === 'LI') start(true, false);
    const next = styleOf(node, st);
    const childLi = inLi || name === 'LI';
    for (let i = 0; i < node.childNodes.length; i++) walk(node.childNodes[i], next, childLi);
    if (isBlock) open = null;
  };
  for (let i = 0; i < root.childNodes.length; i++) walk(root.childNodes[i], { bold: false, italic: false }, false);
  return normalizeQuoteRich(blocks);
};

const escHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const runHtml = (r: QuoteRichRun): string => {
  let h = escHtml(r.text);
  if (r.italic) h = `<i>${h}</i>`;
  if (r.bold) h = `<b>${h}</b>`;
  const c = normalizeRichColor(r.color);
  if (c) h = `<font color="${c}">${h}</font>`;
  return h;
};

/** Startinhalt des Editors. Text escaped, Farben nur als geprueftes '#rrggbb'. */
export const quoteRichToHtml = (blocks: QuoteRichBlock[]): string => {
  let html = '';
  let inList = false;
  let inLi = false;
  // Endet ein Punkt auf einer leeren Zeile (leerer Punkt oder leere
  // Folgezeile), zeigt der Browser sie nur mit einem abschliessenden BR. Erst
  // beim Schliessen entscheiden: ein leerer Punkt MIT Folgezeile braucht kein
  // Platzhalter-BR, sonst entstuende beim Wiedereinlesen eine Leerzeile mehr.
  let liEndsEmpty = false;
  const closeLi = () => {
    if (inLi) html += liEndsEmpty ? '<br></li>' : '</li>';
    inLi = false;
    liEndsEmpty = false;
  };
  for (const b of blocks) {
    const runs = b.runs.map(runHtml).join('');
    if (b.list && b.cont && inLi) {
      // Folgezeile: Umbruch im selben Punkt.
      html += `<br>${runs}`;
      liEndsEmpty = !runs;
      continue;
    }
    closeLi();
    if (b.list) {
      if (!inList) html += '<ul>';
      inList = true;
      html += `<li>${runs}`;
      inLi = true;
      liEndsEmpty = !runs;
    } else {
      if (inList) html += '</ul>';
      inList = false;
      html += `<div>${runs || '<br>'}</div>`;
    }
  }
  closeLi();
  if (inList) html += '</ul>';
  return html;
};

// ── Zeilenumbruch fuer das PDF ───────────────────────────────────────────────

export type QuoteRichFontStyle = 'normal' | 'bold' | 'italic' | 'bolditalic';

export interface QuoteRichSegment {
  text: string;
  /** Abstand vom linken Rand der Beschreibung (inkl. Listeneinzug). */
  x: number;
  fontStyle: QuoteRichFontStyle;
  color?: string;
}

export interface QuoteRichLine {
  /** Erste Zeile eines Aufzaehlungspunkts: davor steht der Punkt. */
  bullet: boolean;
  segments: QuoteRichSegment[];
}

type Piece = { text: string; fontStyle: QuoteRichFontStyle; color?: string };

const fontStyleOf = (r: QuoteRichRun, quiet: boolean): QuoteRichFontStyle => {
  if (quiet) return 'italic';
  if (r.bold && r.italic) return 'bolditalic';
  if (r.bold) return 'bold';
  if (r.italic) return 'italic';
  return 'normal';
};

/**
 * Bricht formatierte Bloecke auf `width` um. `measure` liefert die Breite eines
 * Texts in der jeweiligen Schrift (im PDF: doc.getTextWidth). Aufzaehlungen
 * bekommen einen haengenden Einzug von `listIndent`. `quiet` (NEP/gestrichen)
 * setzt alles kursiv und ohne Farbe — wie der bisherige Druck solcher Zeilen.
 */
export const layoutQuoteRich = (
  blocks: QuoteRichBlock[],
  width: number,
  measure: (text: string, fontStyle: QuoteRichFontStyle) => number,
  opts: { listIndent: number; quiet?: boolean },
): QuoteRichLine[] => {
  const quiet = !!opts.quiet;
  const lines: QuoteRichLine[] = [];

  for (const block of blocks) {
    const indent = block.list ? opts.listIndent : 0;
    const avail = Math.max(1, width - indent);
    let first = true;

    const makeLine = (pieces: Piece[]): QuoteRichLine => {
      const segments: QuoteRichSegment[] = [];
      for (const p of pieces) {
        const last = segments[segments.length - 1];
        if (last && last.fontStyle === p.fontStyle && last.color === p.color) last.text += p.text;
        else segments.push({ text: p.text, x: 0, fontStyle: p.fontStyle, ...(p.color ? { color: p.color } : {}) });
      }
      let x = indent;
      for (const s of segments) {
        s.x = x;
        x += measure(s.text, s.fontStyle);
      }
      const line = { bullet: !!block.list && !block.cont && first, segments };
      first = false;
      return line;
    };

    // Woerter (Folgen ohne Leerzeichen, auch ueber Formatwechsel hinweg) und
    // Leerraum dazwischen.
    type Token = { space: boolean; pieces: Piece[]; width: number };
    const tokens: Token[] = [];
    for (const run of block.runs) {
      const fontStyle = fontStyleOf(run, quiet);
      const color = quiet ? undefined : run.color;
      for (const part of run.text.split(/(\s+)/)) {
        if (!part) continue;
        const space = /^\s+$/.test(part);
        const piece: Piece = { text: space ? part.replace(/\s/g, ' ') : part, fontStyle, color };
        const w = measure(piece.text, fontStyle);
        const last = tokens[tokens.length - 1];
        if (last && last.space === space) {
          last.pieces.push(piece);
          last.width += w;
        } else {
          tokens.push({ space, pieces: [piece], width: w });
        }
      }
    }

    let line: Piece[] = [];
    let lineW = 0;
    let gap: Token | null = null;
    const pushLine = () => {
      lines.push(makeLine(line));
      line = [];
      lineW = 0;
    };
    const placeWord = (t: Token) => {
      if (t.width <= avail) {
        line.push(...t.pieces);
        lineW += t.width;
        return;
      }
      // Ein Wort breiter als die Spalte: zeichenweise teilen.
      for (const p of t.pieces) {
        for (const ch of Array.from(p.text)) {
          const w = measure(ch, p.fontStyle);
          if (line.length && lineW + w > avail) pushLine();
          const last = line[line.length - 1];
          if (last && last.fontStyle === p.fontStyle && last.color === p.color) last.text += ch;
          else line.push({ text: ch, fontStyle: p.fontStyle, color: p.color });
          lineW += w;
        }
      }
    };

    for (const t of tokens) {
      if (t.space) {
        // Leerraum am Zeilenanfang faellt weg, sonst merken bis zum naechsten Wort.
        if (line.length) gap = t;
        continue;
      }
      if (!line.length) {
        placeWord(t);
      } else if (lineW + (gap?.width ?? 0) + t.width <= avail + 1e-6) {
        if (gap) {
          line.push(...gap.pieces.map((p) => ({ ...p })));
          lineW += gap.width;
        }
        line.push(...t.pieces.map((p) => ({ ...p })));
        lineW += t.width;
      } else {
        pushLine();
        placeWord(t);
      }
      gap = null;
    }
    // Leerer Block = Leerzeile (beim Aufzaehlungspunkt: leerer Punkt).
    if (line.length || first) pushLine();
  }
  return lines;
};
